import React from "react";

// Mui Styles
import EmployeeShoppingItemDialog from "@/components/Dialog/employeeShoppingItemDialog";
import { StyledPaper } from "@/styles/mui/globalStyles";
import {
  TableListTheme,
  employeeShoppingTheme,
} from "@/styles/mui/tableListTheme";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { ThemeProvider } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid";

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

// Config

// CSS
import "@/styles/page/employeeShopping.css";
import "@/styles/page/systemSettingProductPage.css";

// external library
import { PhotoSlider } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";

// Navigate
import { useNavigate } from "react-router-dom";

// Components
import DataTable from "@/components/dataTable";
import Layout from "@/components/layout";
import Title from "@/components/title";
import { EmployeeShoppingTableColumn } from "@/config/tableColumnConfig";

// Mui Icon

import DownloadIcon from "@mui/icons-material/Download";
import FindInPageIcon from "@mui/icons-material/FindInPage";

// Fetch
import {
  fetchEmployeeDiscountReport,
  getEmployeeDiscountInvoice,
  getEmployeeDiscountPreSignedUrl,
  updateEmployeeDiscountInvoiceApprove,
  updateEmployeeDiscountInvoiceVerified,
} from "@/fetch/fetchEmployeePurchase";

// Moment
import { useCurrentAction } from "@/hooks/user";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import "moment/locale/zh-hk";

const EmployeeShoppingPage = (props) => {
  const { open, setOpen } = props;
  const actions = useCurrentAction();
  // Navigate
  const navigate = useNavigate();
  const apiRef = useGridApiRef();

  // States
  const [dataTableLoading, setDataTableLoading] = React.useState(false);
  const [discountData, setDiscountData] = React.useState([]);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [dialogData, setDialogData] = React.useState();
  const [snackSeverity, setSnackSeverity] = React.useState("success");
  const [snackMessage, setSnackMessage] = React.useState("");
  const [snackOpen, setSnackOpen] = React.useState(false);
  const [isLoading, setLoading] = React.useState(false);
  const [alertOn, setAlertOn] = React.useState(false);
  const [alertMsg, setAlertMsg] = React.useState();
  const [onChangeData, setOnChangeData] = React.useState({
    month: moment().format("YYYY-MM-DD"),
  });

  const handleCellClick = (data) => {
    const isTarget = ["verified", "approved", "attachment", "signature"].some(
      (item) => data.field === item,
    );

    if (!isTarget) {
      const targetItem = discountData.find((item) => item.id === data.row.id);
      setDialogData(targetItem);
      setIsDialogOpen((prev) => !prev);
    }
  };

  // Fetch
  const fetchAll = async () => {
    try {
      await getEmployeeDiscountInvoice(
        moment(onChangeData.month).startOf("month").format("YYYY-MM-DD"),
        moment(onChangeData.month).endOf("month").format("YYYY-MM-DD"),
      ).then((data) => {
        if (data && data.results) {
          const newData = data.data.map((item) => ({
            ...item,
            fetchAll,
            apiRef,
            setAlertOn,
            setAlertMsg,
            actions,
          }));
          setDiscountData(newData);
        } else {
          setDiscountData([]);
        }
      });
    } catch (error) {
    } finally {
      setLoading(false);
      setDataTableLoading(false);
    }
  };

  // Browser Back
  const handleBeforeUnload = (e) => {
    e.preventDefault();
    const message =
      "Are you sure you want to leave? All provided data will be lost.";
    e.returnValue = message;
    return message;
  };
  const getRowId = (row) => {
    return row.id;
  };
  React.useEffect(() => {
    actions && fetchAll();

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions]);

  return (
    <Layout
      header="StaffManagePage"
      snackOpen={snackOpen}
      snackClose={() => setSnackOpen(false)}
      snackMessage={snackMessage}
      snackSeverity={snackSeverity}
      open={open}
      setOpen={setOpen}
    >
      <Title
        title={`員工購物紀錄`}
        history={() => navigate(-1)}
        children={
          <div>
            <ButtonMenu
              start={moment(onChangeData.month)
                .startOf("month")
                .format("YYYY-MM-DD")}
              end={moment(onChangeData.month)
                .endOf("month")
                .format("YYYY-MM-DD")}
            />
          </div>
        }
      />
      <EmployeeShoppingItemDialog
        open={isDialogOpen}
        onClose={setIsDialogOpen}
        data={dialogData}
      />
      <StyledPaper page={"yes"}>
        <Box sx={{ m: 3, width: "100%" }}>
          <Stack direction={"row"} alignItems={"center"} spacing={2}>
            <Stack alignItems={"center"}>
              <FormControl fullWidth>
                <DateTimePicker
                  slotProps={{
                    popper: {
                      sx: {
                        ".MuiPaper-root": {
                          border: "1px solid blue",
                          borderRadius: "10px",
                        },
                      },
                    },
                  }}
                  inputFormat="MM/YYYY"
                  renderInput={(props) => (
                    <TextField
                      color="success"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderBottomRightRadius: 0,
                          borderBottomLeftRadius: 0,
                        },
                      }}
                      {...props}
                    />
                  )}
                  label="Month"
                  openTo="month"
                  views={["year", "month"]}
                  value={onChangeData.month}
                  onChange={(event) => {
                    setOnChangeData({
                      ...onChangeData,
                      month: moment(event._d).format("YYYY-MM-DD"),
                    });
                  }}
                />
              </FormControl>
              <LoadingButton
                sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
                fullWidth
                onClick={() => {
                  setDataTableLoading(true);
                  setLoading(true);
                  fetchAll();
                }}
                size="small"
                loading={isLoading}
                loadingIndicator="Loading…"
                variant="contained"
                color="success"
              >
                搜尋
              </LoadingButton>
            </Stack>
            <ThemeProvider theme={employeeShoppingTheme}>
              <Stack>
                <Fade
                  in={alertOn}
                  unmountOnExit
                  onEnter={() => setTimeout(() => setAlertOn(false), 3000)}
                >
                  <Alert severity="warning">{alertMsg}</Alert>
                </Fade>
              </Stack>
            </ThemeProvider>
          </Stack>
        </Box>
        <ThemeProvider theme={TableListTheme}>
          <DataTable
            loading={dataTableLoading}
            getRowId={getRowId}
            apiRef={apiRef}
            // newEditingApi={true}
            rows={discountData}
            columns={EmployeeShoppingTableColumn}
            pageSize={100}
            onCellClick={handleCellClick}
          />
        </ThemeProvider>
      </StyledPaper>
    </Layout>
  );
};

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
    </GridToolbarContainer>
  );
}

export default EmployeeShoppingPage;

function ButtonMenu({ start, end }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState(moment(start));

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const downLoadTemplate = async (start, end, typeId, selectedDate) => {
    try {
      await fetchEmployeeDiscountReport(start, end, typeId, selectedDate).then(
        (response) => {
          const url = window.URL.createObjectURL(new Blob([response]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `員工購物紀錄(${moment(start).format("YYYY-MM")} - ${moment(
              end,
            ).format("YYYY-MM")}).xlsx`,
          );
          document.body.appendChild(link);
          link.click();
        },
      );
    } catch (err) {
      console.log(err.message);
    }
  };

  return (
    <>
      <IconButton
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        <DownloadIcon />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <DatePicker
          minDate={start}
          maxDate={end}
          label={"送貲日期"}
          value={selectedDate}
          onChange={setSelectedDate}
          renderInput={(params) => (
            <TextField {...params} sx={{ width: "100%" }} />
          )}
        ></DatePicker>
        {[
          { name: "By Product", id: 1 },
          { name: "By User", id: 2 },
        ].map((item, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              downLoadTemplate(start, end, item.id, selectedDate);
              handleClose();
            }}
          >
            {item.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export function EmployeeShoppingImagePreview(props) {
  const maskClassName =
    props?.field === "signature" ? "PhotoView-Slider__Backdrop2" : "";
  const images = props.value ? props.value : [];
  const [visible, setVisible] = React.useState(false);
  const [index, setIndex] = React.useState(0);
  const [signedImages, setSignedImage] = React.useState([]);
  const getPreSignedUrl = async (signatureArr) => {
    await getEmployeeDiscountPreSignedUrl(signatureArr).then((data) => {
      setSignedImage(data.data);
      setVisible(true);
    });
  };

  return (
    <React.Fragment>
      <IconButton
        onClick={() => getPreSignedUrl(images)}
        disabled={images.length === 0 ? true : false}
      >
        <FindInPageIcon fontSize="inherit" />
      </IconButton>
      <PhotoSlider
        maskClassName={maskClassName}
        loadingElement={<CircularProgress size="3rem" color="inherit" />}
        maskOpacity={0.5}
        images={signedImages.map((item, index) => ({
          src: item,
          key: index,
        }))}
        visible={visible}
        onClose={() => setVisible(false)}
        index={index}
        onIndexChange={setIndex}
      />
    </React.Fragment>
  );
}

export function ApproveButton(props) {
  const { row } = props;
  const { apiRef } = row;
  const approved = props?.value;

  const updateApprove = async (approved, invoiceId) => {
    try {
      await updateEmployeeDiscountInvoiceApprove(approved, invoiceId);
      apiRef.current.updateRows([{ id: invoiceId, ...row, approved: true }]);
    } catch (error) {
      row.setAlertMsg("更新失敗");
      row.setAlertOn(true);
    }
  };
  const hanldeOnClick = () => {
    if (row.approved) {
      return false;
    }
    if (row.actions?.hr_approve) {
      if (row.signature && row.approved) {
        row.setAlertMsg("不能修改");
        row.setAlertOn(true);
      } else if (!row.approved) {
        const answer = window.confirm(`確定將${row.id}更變為已核對？`);
        if (answer) {
          updateApprove(!row.approved, row.id);
        }
      }
    } else {
      row.setAlertMsg("Permission Deny");
      row.setAlertOn(true);
    }
  };
  return (
    <Chip
      disabled={row.attachment?.length > 0 ? false : true}
      sx={{ minWidth: 65, flex: 1 }}
      color={approved ? "success" : "error"}
      label={`${approved ? "已核准" : "未核准"}`}
      variant="outlined"
      onClick={hanldeOnClick}
    />
  );
}

export function VerifyButton(props) {
  const { row } = props;
  const { apiRef } = row;
  const verified = props?.value;
  // const storage = window.localStorage;
  // const userRole = storage.getItem("userRole");

  const updateVerified = async (verified, invoiceId) => {
    try {
      await updateEmployeeDiscountInvoiceVerified(verified, invoiceId);
      apiRef.current.updateRows([{ id: invoiceId, ...row, verified: true }]);
    } catch (error) {
      row.setAlertMsg("更新失敗");
      row.setAlertOn(true);
    }
  };
  const hanldeOnClick = () => {
    if (row.verified) {
      return false;
    }
    if (row.actions?.account_approve) {
      if (row.signature && row.verified) {
        row.setAlertMsg("不能修改");
        row.setAlertOn(true);
      } else if (!row.verified) {
        const answer = window.confirm(`確定將${row.id}更變為已核對？`);
        if (answer) {
          updateVerified(!row.verified, row.id);
        }
      }
    } else {
      row.setAlertMsg("Permission Deny");
      row.setAlertOn(true);
    }
  };
  return (
    <Chip
      disabled={!row.approved ? true : false}
      sx={{ minWidth: 65, flex: 1 }}
      color={verified ? "success" : "error"}
      label={`${verified ? "已核對" : "未核對"}`}
      variant="outlined"
      onClick={hanldeOnClick}
    />
  );
}
